import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams } from 'react-router-dom';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { Seo, SubmissionLoader } from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { AssetDetailsPartial } from '@Components/Partial/Product/ContentId';
import {
  initiateFetchAssetDetailsRequest,
  clearContentIdAssetDetailsRequest,
  updateContentIdAssetDetailsRequest,
  initiateFetchAssetMatchPolicyRequest,
  clearContentIdAssetMatchPolicyRequest,
  updateContentIdAssetMatchPolicyRequest,
  initiateFetchAssetClaimsCount,
  initiateReleaseAllAssetClaims,
  initiateFetchActiveAssetReleaseRequest,
  initiateRevertClaimReleaseRequest,
} from '@Actions';
import { getMatchPolicyAction } from '@Helpers/util';

const AssetDetailsPage = ({
  small,
  loading,
  submitLoading,
  assetDetails,
  fetchAssetDetails,
  clearAssetDetails,
  updateAssetDetails,
  matchPolicy,
  matchPolicyLoading,
  fetchMatchPolicy,
  clearMatchPolicy,
  updateMatchPolicy,
  fetchAssetClaimsCount,
  releaseAllAssetClaims,
  fetchActiveReleaseRequest,
  revertReleaseRequest,
  location,
}) => {
  const { id } = useParams();
  const [formValues, setFormValues] = React.useState(null);
  const [matchPolicyFormValue, setMatchPolicyFormValue] = React.useState(null);
  const [releaseClaimsCount, setReleaseClaimsCount] = React.useState(null);
  const [releaseCountLoading, setReleaseCountLoading] = React.useState(false);
  const [activeReleaseRequest, setActiveReleaseRequest] = React.useState(null);
  const { theme: { page }, isDarkMode } = useAppTheme();

  React.useEffect(() => {
    fetchAssetDetails(id);
    fetchMatchPolicy(id);
    return () => {
      clearAssetDetails();
      clearMatchPolicy();
    };
  }, [id]);

  React.useEffect(() => {
    fetchActiveReleaseRequest(id).then(setActiveReleaseRequest);
  }, [id]);

  // Sync form values with asset details
  React.useEffect(() => {
    if (assetDetails) {
      setFormValues({
        title: assetDetails.metadata?.title || '',
        description: assetDetails.metadata?.description || '',
        notes: assetDetails.metadata?.notes || '',
        customId: assetDetails.metadata?.customId || '',
      });
    }
  }, [assetDetails]);

  // Sync match policy form value with match policy data
  React.useEffect(() => {
    if (matchPolicy) {
      const action = getMatchPolicyAction(matchPolicy);
      setMatchPolicyFormValue(action);
    }
  }, [matchPolicy]);

  // Check if any field has changed
  const submitEnabled = React.useMemo(() => {
    if (!assetDetails || !formValues) return false;
    const meta = assetDetails.metadata || {};
    return (
      formValues.title !== (meta.title || '')
      || formValues.description !== (meta.description || '')
      || formValues.notes !== (meta.notes || '')
      || formValues.customId !== (meta.customId || '')
    );
  }, [assetDetails, formValues]);

  // Check if match policy has changed
  const matchPolicySubmitEnabled = React.useMemo(() => {
    if (!matchPolicy || !matchPolicyFormValue) return false;
    const currentAction = getMatchPolicyAction(matchPolicy);
    return matchPolicyFormValue !== currentAction;
  }, [matchPolicy, matchPolicyFormValue]);

  const handleSubmit = () => {
    if (!formValues) return;
    // Always send all metadata fields, even unchanged
    const payload = {
      metadata: {
        title: formValues.title,
        description: formValues.description,
        notes: formValues.notes,
        customId: formValues.customId,
      },
    };
    updateAssetDetails(id, payload);
  };

  const handleMatchPolicySubmit = () => {
    if (!matchPolicyFormValue) return;
    const payload = {
      action: matchPolicyFormValue,
    };
    updateMatchPolicy(id, payload);
  };

  const handleOpenReleaseModal = async () => {
    setReleaseClaimsCount(null);
    setReleaseCountLoading(true);
    const count = await fetchAssetClaimsCount(id);
    setReleaseClaimsCount(count);
    setReleaseCountLoading(false);
  };

  const handleReleaseAllClaims = async (onClose) => {
    const result = await releaseAllAssetClaims(id);
    if (result) {
      // Refresh so the button switches from "Release all claims" to "Revert release"
      fetchActiveReleaseRequest(id).then(setActiveReleaseRequest);
      if (onClose) onClose();
    }
  };

  const handleRevertReleaseRequest = async (onClose) => {
    if (!activeReleaseRequest) return;
    const claims = (activeReleaseRequest.items || []).map((item) => ({
      /* eslint-disable camelcase */
      claim_id: item.claimId,
      /* eslint-enable camelcase */
    }));
    if (claims.length === 0) return;
    const result = await revertReleaseRequest({ claims });
    if (result) {
      setActiveReleaseRequest(null);
      if (onClose) onClose();
    } else {
      // Re-fetch to sync button state (request may now be UNDER_REVIEW)
      fetchActiveReleaseRequest(id).then(setActiveReleaseRequest);
      if (onClose) onClose();
    }
  };

  const pageTitle = assetDetails ? `Asset details - ${assetDetails?.metadata?.title || assetDetails.id}` : 'Asset details';
  const verticalPadding = small ? '1.5rem' : '2rem';
  const horizontalPadding = small ? '1rem' : '1.5rem';
  const usePageBg = isDarkMode ? page.background : page.altBg;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: pageTitle,
        subTitle: 'Content ID',
      }}
    >
      <Seo />
      {submitLoading && (
        <SubmissionLoader
          textColor={page.modalText}
          loaderColor={page.highlight}
          message="Updating..."
          contentWidth="20rem"
        />
      )}
      <Box
        flex
        background={usePageBg}
        pad={{ vertical: verticalPadding, horizontal: horizontalPadding }}
      >
        <AssetDetailsPartial
          small={small}
          asset={assetDetails}
          loading={loading}
          formValues={formValues || {
            title: '',
            description: '',
            notes: '',
            customId: '',
          }}
          setFormValues={setFormValues}
          onSubmit={handleSubmit}
          submitEnabled={submitEnabled}
          matchPolicy={matchPolicy}
          matchPolicyLoading={matchPolicyLoading}
          matchPolicyFormValue={matchPolicyFormValue}
          setMatchPolicyFormValue={setMatchPolicyFormValue}
          onMatchPolicySubmit={handleMatchPolicySubmit}
          matchPolicySubmitEnabled={matchPolicySubmitEnabled}
          onOpenReleaseModal={handleOpenReleaseModal}
          onReleaseAllClaims={handleReleaseAllClaims}
          releaseClaimsCount={releaseClaimsCount}
          releaseCountLoading={releaseCountLoading}
          releaseAllSubmitting={submitLoading}
          activeReleaseRequest={activeReleaseRequest}
          onRevertReleaseRequest={handleRevertReleaseRequest}
        />
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    assetDetails: state.contentIdAssets.assetDetails,
    loading: state.fetchLoader.dataLoading,
    submitLoading: state.fetchLoader.submissionLoading,
    matchPolicy: state.contentIdAssets.matchPolicy,
    matchPolicyLoading: state.fetchLoader.secondaryDataLoading,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchAssetDetails: initiateFetchAssetDetailsRequest,
    clearAssetDetails: clearContentIdAssetDetailsRequest,
    updateAssetDetails: updateContentIdAssetDetailsRequest,
    fetchMatchPolicy: initiateFetchAssetMatchPolicyRequest,
    clearMatchPolicy: clearContentIdAssetMatchPolicyRequest,
    updateMatchPolicy: updateContentIdAssetMatchPolicyRequest,
    fetchAssetClaimsCount: initiateFetchAssetClaimsCount,
    releaseAllAssetClaims: initiateReleaseAllAssetClaims,
    fetchActiveReleaseRequest: initiateFetchActiveAssetReleaseRequest,
    revertReleaseRequest: initiateRevertClaimReleaseRequest,
  }, dispatch);
}

AssetDetailsPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  submitLoading: PropTypes.bool.isRequired,
  fetchAssetDetails: PropTypes.func.isRequired,
  clearAssetDetails: PropTypes.func.isRequired,
  updateAssetDetails: PropTypes.func.isRequired,
  assetDetails: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.string,
    timeCreated: PropTypes.string,
    label: PropTypes.arrayOf(PropTypes.string),
    metadata: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      notes: PropTypes.string,
      customId: PropTypes.string,
    }),
  }),
  matchPolicy: PropTypes.shape({
    kind: PropTypes.string,
    policyId: PropTypes.string,
    rules: PropTypes.arrayOf(PropTypes.shape({
      action: PropTypes.string,
      subaction: PropTypes.array,
      conditions: PropTypes.object,
      appliesGlobally: PropTypes.bool,
    })),
  }),
  matchPolicyLoading: PropTypes.bool.isRequired,
  fetchMatchPolicy: PropTypes.func.isRequired,
  clearMatchPolicy: PropTypes.func.isRequired,
  updateMatchPolicy: PropTypes.func.isRequired,
  fetchAssetClaimsCount: PropTypes.func.isRequired,
  releaseAllAssetClaims: PropTypes.func.isRequired,
  fetchActiveReleaseRequest: PropTypes.func.isRequired,
  revertReleaseRequest: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(AssetDetailsPage));
